import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { mapServiceError } from '../utils/serviceErrors';
import {
  // Pets
  createPet as createPetFirebase,
  updatePet as updatePetFirebase,
  deletePet as deletePetFirebase,
  getPetsByOwner,
  // Bookings
  createBooking as createBookingFirebase,
  updateBookingStatus,
  getBookingsByOwner,
  getBookingsByCaregiver,
  // Offline Jobs
  createOfflineJob as createOfflineJobFirebase,
  updateOfflineJob as updateOfflineJobFirebase,
  deleteOfflineJob as deleteOfflineJobFirebase,
  getOfflineJobsByUser,
  getAllOfflineJobs,
  // Expenses
  createExpense as createExpenseFirebase,
  updateExpense as updateExpenseFirebase,
  deleteExpense as deleteExpenseFirebase,
  getExpensesByCaregiver,
  // Reviews
  // (createReview and getReviewsByCaregiver removed - unused)
  // Messages
  sendMessageFirebase,
  getMessagesByUser,
  // (getMessagesByBooking, markMessageAsRead, deleteMessage removed - unused)
  subscribeToUserMessages,
  // Coupons
  getAllCoupons,
  createCoupon as createCouponFirebase,
  updateCoupon as updateCouponFirebase,
  deleteCoupon as deleteCouponFirebase,
  validateAndRedeemCoupon,
  getUserFeatureAccess as getUserFeatureAccessFirebase,
  updateUserFeatureAccess as updateUserFeatureAccessFirebase,
  getAllCouponRedemptions,
  getUserCouponRedemptions,
  getCouponRedemptions,
  adminAssignCouponToUser,
  resetCouponUsageCount as resetCouponUsageCountFirebase,
  // Caregivers
  getAllCaregivers,
  // (getCaregiverById, updateCaregiverProfile removed - unused)
  // Users
  getAllUsers,
  updateUserSubscriptionPrice as updateUserSubscriptionPriceFirebase,
  updateUserRole as updateUserRoleFirebase,
  suspendUser as suspendUserFirebase,
  activateUser as activateUserFirebase,
  deleteUser as deleteUserFirebase,
  createUserByAdmin as createUserByAdminFirebase,
  // Financial Analytics
  getFinancialAnalytics,
  // Admin - Role Management
  getAllRoles,
  createAdminRole,
  updateAdminRole,
  deleteAdminRole,
  assignRoleToUser,
  getUserPermissions,
  setUserCustomPermissions,
  initializePredefinedRoles,
  // User Profiles
  // (createUserProfile removed - unused)
  getUserProfile,
  updateUserProfile,
  updatePrivacySettings,
  addProfilePhoto,
  removeProfilePhoto,
  // Activities
  createActivity,
  getUserActivities,
  getPublicActivities,
  // (deleteActivity removed - unused)
  // Followers
  followUser as followUserFirebase,
  unfollowUser as unfollowUserFirebase,
  isFollowing,
  getFollowers,
  getFollowing,
  getFollowerCount,
  getFollowingCount,
  // Badges
  awardBadge as awardBadgeFirebase,
  getUserBadges,
  checkAndAwardBadges,
  getAllBadges as getAllBadgesFirebase,
  revokeBadge as revokeBadgeFirebase,
  // Blogs
  createBlog as createBlogFirebase,
  updateBlog as updateBlogFirebase,
  deleteBlog as deleteBlogFirebase,
  getAllBlogs,
  getPublishedBlogs,
  getBlogBySlug,
  incrementBlogViews
} from '../services/firestoreService';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user, updateUser } = useAuth();
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [caregiversLoading, setCaregiversLoading] = useState(true);
  const [caregiversError, setCaregiversError] = useState(null);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [userDataError, setUserDataError] = useState(null);
  
  // Data states
  const [pets, setPets] = useState([]);

  const [caregivers, setCaregivers] = useState([]);

  const [bookings, setBookings] = useState([]);

  // Messages - Start empty, will be fetched from Firebase
  const [messages, setMessages] = useState([]);

  const [reviews, setReviews] = useState([]);

  // Offline Jobs - Track external jobs not booked through platform
  const [offlineJobs, setOfflineJobs] = useState([]); // Start empty, will be fetched from Firestore
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);

  // Coupons for feature access
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]); // Start empty, will be fetched from Firebase
  const [couponRedemptions, setCouponRedemptions] = useState([]); // All redemptions for reporting

  // User Feature Access (coupon-based subscriptions)
  const [userFeatureAccess, setUserFeatureAccess] = useState([]);

  // Platform Settings
  const [platformSettings] = useState({
    offlineJobsEnabled: true,
    offlineJobsMonthlyFee: 29,
    offlineJobsCouponRequired: true
  });

  // Expenses state
  const [expenses, setExpenses] = useState([]);
  
  // Profile-related states
  const [userProfile, setUserProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [badges, setBadges] = useState([]);

  // Firebase Fetch Functions
  const fetchPets = async () => {
    if (!user || user.role !== 'owner') return;
    setLoading(true);
    try {
      const result = await getPetsByOwner(user.uid || user.id);
      if (result.success) {
        setPets(result.pets || []);
      }
    } catch (err) {
      console.error('Error fetching pets:', err);
      const msg = mapServiceError(err.message).message;
      setError(err.message);
      setUserDataError(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    console.log('📅 fetchBookings called, user:', user);
    if (!user) {
      console.log('❌ No user, skipping booking fetch');
      return;
    }
    
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      let result;
      if (user.role === 'owner') {
        console.log('📅 Fetching bookings for owner:', userId);
        result = await getBookingsByOwner(userId);
      } else if (user.role === 'caregiver') {
        console.log('📅 Fetching bookings for caregiver:', userId);
        result = await getBookingsByCaregiver(userId);
      } else {
        console.log('❌ User role not owner or caregiver:', user.role);
      }
      
      console.log('📅 Fetch result:', result);
      if (result?.success) {
        setBookings(result.bookings || []);
        console.log('✅ Set bookings:', result.bookings?.length || 0);
      } else {
        console.log('❌ No successful result');
        setBookings([]);
      }
    } catch (err) {
      console.error('❌ Error fetching bookings:', err);
      const msg = mapServiceError(err.message).message;
      setError(err.message);
      setUserDataError(msg);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOfflineJobs = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      console.log('📥 Fetching offline jobs for user:', userId);
      const result = await getOfflineJobsByUser(userId);
      console.log('📥 Fetch result:', result);
      if (result.success) {
        setOfflineJobs(result.jobs || []);
        console.log('✅ Loaded offline jobs:', result.jobs?.length || 0);
      } else {
        console.error('❌ Failed to fetch offline jobs:', result.error);
      }
    } catch (err) {
      console.error('❌ Error fetching offline jobs:', err);
      const msg = mapServiceError(err.message).message;
      setError(err.message);
      setUserDataError(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    console.log('💰 fetchExpenses called, user:', user);
    if (!user) {
      console.log('❌ No user, skipping expense fetch');
      return;
    }
    if (user.role !== 'caregiver') {
      console.log('❌ User is not caregiver, skipping expense fetch');
      return;
    }
    
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      console.log('💰 Fetching expenses for userId:', userId);
      const result = await getExpensesByCaregiver(userId);
      console.log('💰 Fetch result:', result);
      if (result.success) {
        setExpenses(result.expenses || []);
        console.log('✅ Set expenses:', result.expenses?.length || 0);
      } else {
        console.error('❌ Failed to fetch expenses:', result.error);
        setExpenses([]);
      }
    } catch (err) {
      console.error('❌ Error fetching expenses:', err);
      const msg = mapServiceError(err.message).message;
      setError(err.message);
      setUserDataError(msg);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaregivers = async () => {
    setCaregiversLoading(true);
    setCaregiversError(null);
    try {
      const result = await getAllCaregivers();
      if (result.success) {
        setCaregivers(result.caregivers || []);
      } else {
        setCaregivers([]);
        setCaregiversError(result.error || 'Unable to load caregivers');
      }
    } catch (err) {
      console.error('Error fetching caregivers:', err);
      setCaregivers([]);
      setCaregiversError(err.message);
    } finally {
      setCaregiversLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const result = await getAllUsers();
      if (result.success) {
        setUsers(result.users || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      console.log('📥 Fetching coupons from Firebase...');
      const result = await getAllCoupons();
      if (result.success) {
        setCoupons(result.coupons || []);
        console.log('✅ Loaded coupons:', result.coupons?.length || 0);
      } else {
        console.error('❌ Failed to fetch coupons:', result.error);
        setCoupons([]);
      }
    } catch (err) {
      console.error('❌ Error fetching coupons:', err);
      setError(err.message);
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubscriptionPrice = async (userId, subscriptionPrice) => {
    try {
      const result = await updateUserSubscriptionPriceFirebase(userId, subscriptionPrice);
      if (result.success) {
        // Refresh users after update
        await fetchUsers();
      }
      return result;
    } catch (err) {
      console.error('Error updating subscription price:', err);
      return { success: false, error: err.message };
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const result = await updateUserRoleFirebase(userId, newRole);
      if (result.success) {
        // Refresh users after update
        await fetchUsers();
      }
      return result;
    } catch (err) {
      console.error('Error updating user role:', err);
      return { success: false, error: err.message };
    }
  };

  const refreshUserData = useCallback(async () => {
    if (!user) return;
    setUserDataLoading(true);
    setUserDataError(null);
    setPets([]);
    setBookings([]);
    setOfflineJobs([]);
    setExpenses([]);
    setMessages([]);
    setReviews([]);

    try {
      await Promise.all([
        fetchPets(),
        fetchBookings(),
        fetchOfflineJobs(),
        fetchExpenses(),
        fetchMessages(),
      ]);
    } finally {
      setUserDataLoading(false);
    }
    // fetch* helpers read `user` from closure; stable enough for retry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch data when user logs in
  useEffect(() => {
    if (user) {
      refreshUserData();
      fetchCaregivers();
    } else {
      // Clear all data when user logs out
      setPets([]);
      setBookings([]);
      setOfflineJobs([]);
      setExpenses([]);
      // DON'T clear caregivers - they should remain visible for guest browsing
      setMessages([]);
      setReviews([]);
      setCoupons([]);
      setUserFeatureAccess([]);
      setUserDataLoading(false);
      setUserDataError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Caregivers are public so guests can browse before signing up
  useEffect(() => {
    fetchCaregivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coupons and redemptions require auth — fetching them as a guest only
  // produces permission-denied noise on every page load
  useEffect(() => {
    if (!user) return;
    fetchCoupons();
    fetchCouponRedemptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.id]);

  // Fetch blogs — published for public, all posts for admin
  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.uid, user?.id]);

  // Real-time listener for messages
  useEffect(() => {
    if (!user) return;
    
    const userId = user.uid || user.id;
    console.log('🔔 Setting up real-time message listener for:', userId);
    
    // Subscribe to real-time updates
    const unsubscribe = subscribeToUserMessages(userId, () => {
      fetchMessages(); // Refresh messages when changes occur
    });
    
    // Cleanup on unmount or user change
    return () => {
      console.log('🔔 Cleaning up message listener');
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  
  // Fetch blogs function
  const fetchBlogs = async () => {
    setBlogsLoading(true);
    try {
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      const result = isAdmin ? await getAllBlogs() : await getPublishedBlogs();
      if (result.success) {
        setBlogs(result.blogs);
      } else {
        console.error('Error fetching blogs:', result.error);
        setBlogs([]);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setBlogs([]);
    } finally {
      setBlogsLoading(false);
    }
  };

  // Helper functions - Pets
  const addPet = async (pet) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await createPetFirebase(user.uid || user.id, pet);
      if (result.success) {
        await fetchPets(); // Refresh list from Firebase
        return { success: true, petId: result.petId };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error adding pet:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updatePet = async (id, updates) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await updatePetFirebase(id, updates);
      if (result.success) {
        await fetchPets(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error updating pet:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deletePet = async (id) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await deletePetFirebase(id);
      if (result.success) {
        await fetchPets(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error deleting pet:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Helper functions - Bookings
  const createBooking = async (bookingData) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await createBookingFirebase(bookingData);
      if (result.success) {
        await fetchBookings(); // Refresh list from Firebase
        return { success: true, bookingId: result.bookingId };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateBooking = async (id, updates) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await updateBookingStatus(id, updates.status, updates);
      if (result.success) {
        await fetchBookings(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error updating booking:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for current user
  const fetchMessages = async () => {
    console.log('💬 fetchMessages called, user:', user);
    if (!user) {
      console.log('❌ No user, skipping message fetch');
      return;
    }
    
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      console.log('💬 Fetching messages for userId:', userId);
      const result = await getMessagesByUser(userId);
      console.log('💬 Fetch result:', result);
      if (result.success) {
        setMessages(result.messages || []);
        console.log('✅ Set messages:', result.messages?.length || 0);
      } else {
        console.error('❌ Failed to fetch messages:', result.error);
        setMessages([]);
      }
    } catch (err) {
      console.error('❌ Error fetching messages:', err);
      const msg = mapServiceError(err.message).message;
      setError(err.message);
      setUserDataError(msg);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Send a new message
  const sendMessage = async (messageData) => {
    if (!user) {
      console.error('❌ User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    
    setLoading(true);
    try {
      const result = await sendMessageFirebase(messageData);
      
      if (result.success) {
        await fetchMessages(); // Refresh message list
        return { success: true, messageId: result.messageId };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const addReview = (reviewData) => {
    const newReview = {
      ...reviewData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    setReviews(prev => [...prev, newReview]);
    return newReview;
  };

  // Expense functions
  const createExpense = async (expenseData) => {
    if (!user) {
      console.error('❌ User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      const result = await createExpenseFirebase(userId, expenseData);
      
      if (result.success) {
        await fetchExpenses(); // Refresh list from Firebase
        return { success: true, expenseId: result.expenseId };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('❌ Error creating expense:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateExpense = async (expenseId, updates) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await updateExpenseFirebase(expenseId, updates);
      if (result.success) {
        await fetchExpenses(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error updating expense:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await deleteExpenseFirebase(expenseId);
      if (result.success) {
        await fetchExpenses(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Offline Jobs functions
  const addOfflineJob = async (jobData) => {
    if (!user) {
      console.error('❌ User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      console.log('💾 Creating offline job for user:', userId, jobData);
      const result = await createOfflineJobFirebase(userId, jobData);
      console.log('💾 Create result:', result);
      
      if (result.success) {
        console.log('✅ Job created, refreshing list...');
        await fetchOfflineJobs(); // Refresh list from Firebase
        return { success: true, jobId: result.jobId };
      }
      console.error('❌ Failed to create job:', result.error);
      return { success: false, error: result.error };
    } catch (err) {
      console.error('❌ Error adding offline job:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateOfflineJob = async (id, updates) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await updateOfflineJobFirebase(id, updates);
      if (result.success) {
        await fetchOfflineJobs(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error updating offline job:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteOfflineJob = async (id) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await deleteOfflineJobFirebase(id);
      if (result.success) {
        await fetchOfflineJobs(); // Refresh list from Firebase
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error deleting offline job:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Coupon functions
  const redeemCoupon = async (userId, code) => {
    if (!user) return { success: false, message: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await validateAndRedeemCoupon(userId, code);
      if (result.success) {
        // Refresh feature access data if needed
        return { success: true, message: 'Coupon redeemed successfully!' };
      }
      return { success: false, message: result.error };
    } catch (err) {
      console.error('Error redeeming coupon:', err);
      setError(err.message);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  const checkFeatureAccess = async (userId, feature) => {
    try {
      const result = await getUserFeatureAccessFirebase(userId);
      if (result.success && result.access[feature]) {
        const access = result.access[feature];
        // Check if expired
        if (!access.expiresAt || new Date(access.expiresAt) > new Date()) {
          return access.active;
        }
      }
      return false;
    } catch (err) {
      console.error('Error checking feature access:', err);
      return false;
    }
  };

  const getUserFeatureAccess = async (userId, feature) => {
    try {
      const result = await getUserFeatureAccessFirebase(userId);
      if (result.success && result.access[feature]) {
        return result.access[feature];
      }
      return null;
    } catch (err) {
      console.error('Error getting feature access:', err);
      return null;
    }
  };

  // Admin coupon functions
  const fetchCouponRedemptions = async () => {
    try {
      console.log('📥 Fetching coupon redemptions...');
      const result = await getAllCouponRedemptions();
      if (result.success) {
        setCouponRedemptions(result.redemptions || []);
        console.log('✅ Loaded redemptions:', result.redemptions?.length || 0);
      } else {
        console.error('❌ Failed to fetch redemptions:', result.error);
      }
    } catch (err) {
      console.error('❌ Error fetching redemptions:', err);
    }
  };

  const assignCouponToUser = async (userId, couponCode, userEmail) => {
    try {
      console.log('Admin assigning coupon:', { userId, couponCode, userEmail });
      const result = await adminAssignCouponToUser(userId, couponCode, userEmail);
      if (result && result.success) {
        await fetchCouponRedemptions();
        await fetchCoupons(); // Refresh to update usage count
      }
      return result;
    } catch (err) {
      console.error('Error assigning coupon:', err);
      return { success: false, error: err.message };
    }
  };

  const getUserRedemptions = async (userId) => {
    try {
      const result = await getUserCouponRedemptions(userId);
      return result;
    } catch (err) {
      console.error('Error getting user redemptions:', err);
      return { success: false, error: err.message, redemptions: [] };
    }
  };

  const getCouponUsageDetails = async (couponCode) => {
    try {
      const result = await getCouponRedemptions(couponCode);
      return result;
    } catch (err) {
      console.error('Error getting coupon usage:', err);
      return { success: false, error: err.message, redemptions: [] };
    }
  };

  const resetCouponUsageCount = async (couponCode) => {
    if (!user || user.role !== 'admin') return { success: false, error: 'Unauthorized' };
    try {
      const result = await resetCouponUsageCountFirebase(couponCode);
      if (result.success) {
        await fetchCoupons();
        await fetchCouponRedemptions();
      }
      return result;
    } catch (err) {
      console.error('Error resetting coupon usage:', err);
      return { success: false, error: err.message };
    }
  };

  const createCoupon = async (couponData) => {
    if (!user || user.role !== 'admin') return { success: false, error: 'Unauthorized' };
    setLoading(true);
    try {
      const result = await createCouponFirebase(couponData);
      if (result.success) {
        await fetchCoupons(); // Refresh list after creating
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error creating coupon:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateCoupon = async (couponCode, updates) => {
    if (!user || user.role !== 'admin') return { success: false, error: 'Unauthorized' };
    setLoading(true);
    try {
      const result = await updateCouponFirebase(couponCode, updates);
      if (result.success) {
        await fetchCoupons(); // Refresh list
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error updating coupon:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteCoupon = async (couponCode) => {
    if (!user || user.role !== 'admin') return { success: false, error: 'Unauthorized' };
    setLoading(true);
    try {
      const result = await deleteCouponFirebase(couponCode);
      if (result.success) {
        await fetchCoupons(); // Refresh list
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error deleting coupon:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateUserFeatureAccess = async (userId, feature, enabled, duration = 'lifetime') => {
    if (!user || user.role !== 'admin') return { success: false, error: 'Unauthorized' };
    setLoading(true);
    try {
      const result = await updateUserFeatureAccessFirebase(userId, feature, enabled, duration);
      if (result.success) {
        // Refresh users or feature access data if needed
        await fetchUsers();
        return { success: true, message: result.message };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error updating feature access:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Profile Functions
  const fetchUserProfile = async (userId) => {
    try {
      const result = await getUserProfile(userId);
      if (result.success) {
        if (userId === (user?.uid || user?.id)) {
          setUserProfile(result.profile);
        }
        return result.profile;
      }
      return null;
    } catch (err) {
      console.error('Error fetching profile:', err);
      return null;
    }
  };

  const saveUserProfile = async (userId, updates) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await updateUserProfile(userId, updates);
      if (result.success) {
        // If profilePhoto was updated, also sync it to user.avatar
        if (updates.profilePhoto && user && (user.uid === userId || user.id === userId)) {
          try {
            await updateUser({ avatar: updates.profilePhoto });
          } catch (err) {
            console.error('Error syncing avatar:', err);
            // Don't fail the whole operation if avatar sync fails
          }
        }
        
        await fetchUserProfile(userId);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const togglePrivacy = async (userId, isPublic) => {
    if (!user || (user.uid || user.id) !== userId) {
      return { success: false, error: 'Unauthorized' };
    }
    setLoading(true);
    try {
      const result = await updatePrivacySettings(userId, isPublic);
      if (result.success) {
        await fetchUserProfile(userId);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error toggling privacy:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Activity Functions
  const fetchActivities = async (userId, isPublic = false) => {
    try {
      const result = isPublic 
        ? await getPublicActivities(50)
        : await getUserActivities(userId, 50);
      
      if (result.success) {
        setActivities(result.activities);
        return result.activities;
      }
      return [];
    } catch (err) {
      console.error('Error fetching activities:', err);
      return [];
    }
  };

  const addActivity = async (activityData) => {
    try {
      const result = await createActivity(activityData);
      if (result.success) {
        await fetchActivities(activityData.userId);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error adding activity:', err);
      return { success: false, error: err.message };
    }
  };

  // Follower Functions
  const followUser = async (followerId, followingId) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await followUserFirebase(followerId, followingId);
      if (result.success) {
        await fetchFollowing(followerId);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error following user:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const unfollowUser = async (followerId, followingId) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      const result = await unfollowUserFirebase(followerId, followingId);
      if (result.success) {
        await fetchFollowing(followerId);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Error unfollowing user:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const isFollowingUser = async (followerId, followingId) => {
    try {
      const result = await isFollowing(followerId, followingId);
      return result.isFollowing;
    } catch (err) {
      console.error('Error checking following status:', err);
      return false;
    }
  };

  const fetchFollowers = async (userId) => {
    try {
      const result = await getFollowers(userId, 100);
      if (result.success) {
        setFollowers(result.followers);
        return result.followers;
      }
      return [];
    } catch (err) {
      console.error('Error fetching followers:', err);
      return [];
    }
  };

  const fetchFollowing = async (userId) => {
    try {
      const result = await getFollowing(userId, 100);
      if (result.success) {
        setFollowing(result.following);
        return result.following;
      }
      return [];
    } catch (err) {
      console.error('Error fetching following:', err);
      return [];
    }
  };

  // Badge Functions
  const fetchBadges = async (userId = null) => {
    try {
      // If no userId provided or user is admin, fetch all badges
      let result;
      if (!userId || user?.role === 'admin') {
        result = await getAllBadgesFirebase();
      } else {
        result = await getUserBadges(userId);
      }
      
      if (result.success) {
        setBadges(result.badges);
        return result.badges;
      }
      return [];
    } catch (err) {
      console.error('Error fetching badges:', err);
      return [];
    }
  };

  const checkBadges = async (userId, userData) => {
    try {
      const result = await checkAndAwardBadges(userId, userData);
      if (result.success && result.badgesAwarded.length > 0) {
        await fetchBadges(userId);
      }
      return result;
    } catch (err) {
      console.error('Error checking badges:', err);
      return { success: false, error: err.message };
    }
  };

  const awardBadge = async (userId, badgeId, reason) => {
    try {
      const result = await awardBadgeFirebase(userId, badgeId, reason);
      if (result.success) {
        // Refresh badges if viewing current user or admin view
        if (user?.id === userId || user?.role === 'admin') {
          await fetchBadges(userId);
        }
        // Also fetch all badges for admin view
        if (user?.role === 'admin') {
          const allResult = await getAllBadgesFirebase();
          if (allResult.success) {
            setBadges(allResult.badges);
          }
        }
      }
      return result;
    } catch (err) {
      console.error('Error awarding badge:', err);
      return { success: false, error: err.message };
    }
  };

  const revokeBadge = async (badgeRecordId) => {
    try {
      const result = await revokeBadgeFirebase(badgeRecordId);
      if (result.success && user?.role === 'admin') {
        // Refresh all badges for admin view
        const allResult = await getAllBadgesFirebase();
        if (allResult.success) {
          setBadges(allResult.badges);
        }
      }
      return result;
    } catch (err) {
      console.error('Error revoking badge:', err);
      return { success: false, error: err.message };
    }
  };

  const value = {
    // State
    loading,
    error,
    caregiversLoading,
    caregiversError,
    userDataLoading,
    userDataError,
    
    // Data
    pets,
    caregivers,
    bookings,
    messages,
    reviews,
    offlineJobs,
    expenses,
    coupons,
    couponRedemptions,
    users,
    userFeatureAccess,
    platformSettings,
    
    // Profile Data
    userProfile,
    activities,
    followers,
    following,
    badges,
    
    // Fetch functions
    fetchPets,
    fetchBookings,
    fetchOfflineJobs,
    fetchExpenses,
    fetchCaregivers,
    refreshUserData,
    fetchUsers,
    fetchCoupons,
    fetchCouponRedemptions,
    
    // Pet functions
    addPet,
    updatePet,
    deletePet,
    
    // Booking functions
    createBooking,
    updateBooking,
    
    // Message functions
    fetchMessages,
    sendMessage,
    
    // Review functions
    addReview,

    // Expense functions
    createExpense,
    updateExpense,
    deleteExpense,

    // Offline Jobs functions
    addOfflineJob,
    updateOfflineJob,
    deleteOfflineJob,
    getAllOfflineJobs,

    // Coupon functions
    redeemCoupon,
    checkFeatureAccess,
    getUserFeatureAccess,
    updateUserFeatureAccess,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    assignCouponToUser,
    getUserRedemptions,
    getCouponUsageDetails,
    resetCouponUsageCount,
    
    // User Management
    updateUserSubscriptionPrice,
    updateUserRole,
    suspendUser: suspendUserFirebase,
    activateUser: activateUserFirebase,
    deleteUser: deleteUserFirebase,
    createUserByAdmin: createUserByAdminFirebase,
    
    // Role & Permission Management
    getAllRoles,
    createAdminRole,
    updateAdminRole,
    deleteAdminRole,
    assignRoleToUser,
    getUserPermissions,
    setUserCustomPermissions,
    initializePredefinedRoles,
    
    // Financial Analytics
    getFinancialAnalytics,
    
    // Profile Functions
    fetchUserProfile,
    saveUserProfile,
    togglePrivacy,
    addProfilePhoto,
    removeProfilePhoto,
    
    // Activity Functions
    fetchActivities,
    addActivity,
    
    // Follower Functions
    followUser,
    unfollowUser,
    isFollowingUser,
    fetchFollowers,
    fetchFollowing,
    getFollowerCount,
    getFollowingCount,
    
    // Badge Functions
    fetchBadges,
    checkBadges,
    awardBadge,
    revokeBadge,
    
    // Blog Management
    blogs,
    blogsLoading,
    createFirebaseBlog: createBlogFirebase,
    updateFirebaseBlog: updateBlogFirebase,
    deleteFirebaseBlog: deleteBlogFirebase,
    getAllBlogs,
    getPublishedBlogs,
    getBlogBySlug,
    incrementBlogViews
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

